import React, { useEffect, useState } from 'react'
import { Box, Chip, Grid, Typography } from '@material-ui/core'
import { isEmpty } from 'lodash'

import { Card, ConfirmationDialog, LoadingFeedback } from 'components'
import TemplateCard from '../TemplateStep/components/TemplateCard'

import useStyles from './styles'

import { PermissionsReminderDialog } from './components'

import useAuth from 'hooks/useAuth'
import useFetch from 'hooks/useFetch'
import usePhishing from 'hooks/usePhishing'
import useSnackbar from 'hooks/useSnackbar'

import * as service from 'service'
import { routes } from 'Routes'
import constants from 'constants/index'
import helpers from 'helpers'

const InfoField = ({ label, children, classes }) => (
  <Box className={classes.infoField}>
    <Box component="span" className={classes.infoFieldLabel}>
      <Typography variant="caption" color="textSecondary">
        {label}
      </Typography>
    </Box>
    {children}
  </Box>
)

const ConfirmStep = () => {
  const classes = useStyles()
  const [loading, setLoading] = useState(false)
  const [confirmOpen, setConfirmOpen] = useState(false)
  const [reminderOpen, setReminderOpen] = useState(false)

  const snackbar = useSnackbar()
  const { user, setUser } = useAuth()
  const { phishing, setPhishing, wantsToAdvanceStep, resetAdvanceStep } =
    usePhishing()
  const needsTemplateFetch =
    !phishing?.phishingTemplate && !!phishing?.phishingTemplateId
  const { response: templateResponse } = useFetch(
    service.dponet.phishingTemplates.show,
    phishing?.phishingTemplateId,
    [],
    needsTemplateFetch,
  )

  const { response: courseResponse } = useFetch(
    service.dponet.trainingCourse.show,
    { courseId: phishing?.trainingId },
    [phishing?.trainingId],
    !!phishing?.trainingId,
  )

  const selectedCourse = courseResponse?.data?.course

  useEffect(() => {
    const fetchedTemplate = templateResponse?.data?.phishingTemplate
    if (fetchedTemplate) {
      setPhishing((previous) => ({
        ...previous,
        phishingTemplate: fetchedTemplate,
      }))
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [templateResponse])

  const isDraftDispatch =
    phishing?.scheduleType === constants.phishing.DISPATCH_LATER

  const onSubmit = async () => {
    setLoading(true)
    try {
      if (isDraftDispatch) {
        snackbar.open({
          message: 'Campanha salva como rascunho com sucesso!',
          variant: 'success',
        })
        return {}
      }

      const response = await service.dponet.phishingCampaigns.confirmStep(
        phishing.id,
      )
      snackbar.open({
        message: 'Campanha criada com sucesso!',
        variant: 'success',
      })
      return response?.data?.phishingCampaign
    } catch {
      snackbar.open({
        message: 'Erro ao confirmar campanha.',
        variant: 'error',
      })
      return null
    } finally {
      setLoading(false)
    }
  }

  const publish = async () => {
    const campaign = await onSubmit()
    if (!campaign) return
    service.history.push(routes.phishing.all)
  }

  const handleConfirmDialog = async () => {
    setConfirmOpen(false)
    await publish()
  }

  const handleReminderClose = () => setReminderOpen(false)

  const handleReminderProceed = async (selected) => {
    setReminderOpen(false)
    if (
      selected ===
      constants.phishing.PERMISSIONS_REMINDER_OPTIONS.ALREADY_CONFIGURED
    ) {
      try {
        await service.dponet.users.phishingPermissionReminder(true)
        setUser((previous) => ({
          ...previous,
          phishingPermissionReminder: true,
        }))
      } catch {}
    }
    setConfirmOpen(true)
  }

  useEffect(() => {
    if (wantsToAdvanceStep) {
      if (!isDraftDispatch && !user?.phishingPermissionReminder) {
        setReminderOpen(true)
      } else {
        setConfirmOpen(true)
      }
      resetAdvanceStep()
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [wantsToAdvanceStep])

  return (
    <>
      <LoadingFeedback open={loading} />
      <Card>
        <Box p={3}>
          <Typography variant="h3">Configurações aplicadas</Typography>

          <Box mt={3}>
            <Grid container spacing={2}>
              <Grid item xs={12}>
                <InfoField classes={classes} label="Nome da campanha">
                  <Typography variant="body1">
                    {phishing?.name || '-'}
                  </Typography>
                </InfoField>
              </Grid>

              <Grid item xs={12}>
                <InfoField classes={classes} label="Participantes">
                  {isEmpty(phishing?.recipients) &&
                  isEmpty(phishing?.externalRecipients) ? (
                    <Typography variant="body1">-</Typography>
                  ) : (
                    <Box display="flex" flexWrap="wrap" gridGap={8}>
                      {(phishing?.recipients || []).map((recipient) => (
                        <Chip
                          key={recipient.id}
                          label={recipient.label}
                          size="small"
                          variant="outlined"
                        />
                      ))}
                      {(phishing?.externalRecipients || []).map((recipient) => (
                        <Chip
                          key={recipient.email}
                          label={recipient.name}
                          size="small"
                          variant="outlined"
                        />
                      ))}
                    </Box>
                  )}
                </InfoField>
              </Grid>

              <Grid item xs={12}>
                <InfoField classes={classes} label="Curso de conscientização">
                  <Typography variant="body1">
                    {selectedCourse?.title || '-'}
                  </Typography>
                </InfoField>
              </Grid>
            </Grid>
          </Box>

          <Box mt={4}>
            <Typography variant="h3">Modelo de e-mail selecionado</Typography>
            <Box mt={2}>
              {phishing?.phishingTemplate ? (
                <>
                  <Grid container spacing={2}>
                    <Grid item xs={12}>
                      <InfoField classes={classes} label="Remetente exibido">
                        <Typography variant="body1">
                          {phishing.phishingTemplate.fromName || '-'}
                        </Typography>
                      </InfoField>
                    </Grid>
                  </Grid>
                  <Grid container>
                    <Grid item xs={12} sm={6} md={4} lg={3}>
                      <Box className={classes.templatePreviewScroll} mt={2}>
                        <TemplateCard
                          template={phishing.phishingTemplate}
                          isSelected
                          isReadOnly
                          hideActions
                          noBorder
                          fitContent
                          onSelect={() => {}}
                          onPreview={() => {}}
                        />
                      </Box>
                    </Grid>
                  </Grid>
                </>
              ) : (
                <Typography variant="body1">-</Typography>
              )}
            </Box>
          </Box>

          <Box mt={4}>
            <Typography variant="h3">Agendamento do disparo</Typography>
            <Box mt={2}>
              <Grid container spacing={2}>
                <Grid item xs={12}>
                  <InfoField
                    classes={classes}
                    label="Data e hora do agendamento"
                  >
                    <Typography variant="body1">
                      {constants.phishing.DISPATCH_OPTIONS.find(
                        (option) => option.value === phishing?.scheduleType,
                      )?.label || '-'}
                      {phishing?.scheduleType ===
                        constants.phishing.DISPATCH_SCHEDULED &&
                        phishing?.scheduledAt && (
                          <>
                            {' — '}
                            {helpers.formatters.ptBrFormatDateTime(
                              phishing.scheduledAt,
                            )}
                          </>
                        )}
                    </Typography>
                  </InfoField>
                </Grid>
              </Grid>
            </Box>
          </Box>
        </Box>
      </Card>

      <ConfirmationDialog
        open={confirmOpen}
        setOpen={setConfirmOpen}
        actionAcceptButton={handleConfirmDialog}
        textButton="Confirmar"
        textButtonCancel="Cancelar"
        loading={loading}
        text={
          <Box textAlign="center">
            <Typography variant="h3">Confirmar criação da campanha</Typography>
            <Box mt={1}>
              <Typography variant="body2" color="textSecondary">
                {isDraftDispatch ? (
                  <>
                    A campanha <strong>{phishing?.name}</strong> será salva como
                    rascunho e poderá ser disparada manualmente depois. Deseja
                    continuar?
                  </>
                ) : (
                  <>
                    Ao confirmar, a campanha <strong>{phishing?.name}</strong>{' '}
                    será criada e os e-mails serão disparados conforme o
                    agendamento configurado. Deseja continuar?
                  </>
                )}
              </Typography>
            </Box>
          </Box>
        }
      />
      <PermissionsReminderDialog
        open={reminderOpen}
        onClose={handleReminderClose}
        onProceed={handleReminderProceed}
      />
    </>
  )
}

export default ConfirmStep
